import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { DEPLOYMENTS } from './deployments'
import { getTelemetryEventsBySession } from '../workspace/service/telemetryService'
import { computeStudentAnalytics } from '../instructorDashboard/components/computeSessionStats'
import { autoSegmentEvents } from '../workspaceReplay2/utils/autoSegmentation'
import { getAllUsersInSession } from '../../service/userService'
import { getSessionById } from '../../service/sessionService'
import { getCachedDeployment, setCachedDeployment } from './components/telemetryCache'
import DeploymentCard from './components/DeploymentCard'
import SessionList from './components/SessionList'
import SimulationView from './components/SimulationView'
import useIsMobile from './components/useIsMobile'

// ─── Sidebar Icons ────────────────────────────────────────────────────────────

const AboutIcon = ({ active }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={active ? '#111' : '#999'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
)

const DatasetIcon = ({ active }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={active ? '#111' : '#999'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
  </svg>
)

const ApplicationsIcon = ({ active }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={active ? '#111' : '#999'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
)

const BackIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
)

// ─── Case Study IDs ───────────────────────────────────────────────────────────

const CASE_STUDY = {
  a: { id: '11690', label: 'Student A' },
  b: { id: '11489', label: 'Student B' },
}

// ─── Dataset Allowlist (matches published JSON files — 664 students) ──────────

const DATASET_ALLOWLIST = {
  deployment_1: new Set(['11479','11480','11481','11482','11483','11484','11485','11486','11487','11488','11489','11490','11491','11492','11493','11494','11495','11496','11497','11498','11499','11500','11501','11502','11505','11506','11507','11508','11509','11510','11512','11513','11514','11515','11516','11517','11518','11519','11520','11521','11522','11523','11524','11525','11526','11527','11528','11529','11530','11531','11532','11533','11534','11535','11536','11537','11538','11539','11540','11541','11542','11543','11544','11545','11546','11548','11549','11550','11551','11552','11553','11554','11555','11556','11557','11558','11559','11560','11561','11562','11563','11564','11565','11566','11567','11568','11569','11570','11571','11572','11573','11574','11575','11576','11577','11578','11580','11581','11582','11583','11584','11585','11586','11587','11588','11589','11590','11591','11592','11594','11595','11596','11597','11598','11599','11600','11601','11602','11603','11604','11605','11606','11607','11608','11609','11610','11611','11612','11613','11614','11615','11616','11617','11618','11619','11620','11621','11622','11623','11624','11625','11626','11627','11628','11629','11630','11631','11632','11633','11634','11635','11636','11637','11638','11639','11640','11641','11642','11643','11644','11645','11646','11647','11648','11649','11650','11651','11652','11653','11654','11655','11656','11657','11658','11659','11660','11661','11662','11663','11664','11665','11666','11667','11668','11669','11670','11671','11672','11673','11674']),
  deployment_2: new Set(['11527','11607','11688','11689','11690','11691','11692','11693','11694','11695','11696','11697','11698','11699','11700','11701','11702','11703','11704','11705','11709','11710','11711','11712','11713','11714','11715','11716','11717','11718','11720','11721','11722','11723','11725','11726','11727','11728','11729','11731','11732','11733','11734','11735','11736','11737','11738','11739','11740','11741','11743','11744','11746','11749','11750','11751','11752','11753','11754','11755','11756','11757','11758','11760','11761','11762','11765','11767','11768','11770','11772','11773','11775','11776','11777','11778','11779','11780','11781','11786','11787','11788','11790','11791','11792','11794','11795','11796','11797','11798','11799','11800','11801','11802','11803','11804','11805','11806','11807','11808','11809','11810','11811','11812','11813','11814','11815','11816','11817','11818','11819','11820','11821']),
  deployment_3: new Set(['11951','11952','11954','11955','11956','11957','11958','11959','11960','11961','11962','11963','11964','11965','11966','11967','11968','11969','11970','11971','11972','11973','11976','11977','11978','11979','11980','11981','11982','11983','11984','11985','11986','11987','11988','11989','11990','11991','11992','11993','11994','11995','11996','11997','11998','11999','12000','12001','12002','12003','12004','12005','12006','12007','12008','12009','12010','12011','12012','12013','12014','12015','12016','12017','12018','12019','12020','12021','12022','12023']),
  deployment_4: new Set(['12194','12196','12197','12199','12200','12201','12202','12203','12204','12205','12206','12207','12209','12210','12211','12212','12213','12214','12215','12216','12217','12218','12219','12220','12221','12222','12223','12224','12225','12226','12227','12228','12229','12230','12231','12232','12233','12234','12235','12236','12238','12239','12240','12241','12242','12243','12244','12245','12246']),
  deployment_5: new Set(['12147','12251','12252','12253','12254','12255','12256','12257','12258','12259','12260','12261','12262','12263','12264','12265','12266','12267','12268','12269','12270','12271','12272','12273','12274','12275','12276','12278','12279','12280','12281','12282','12283','12284','12285','12286','12287','12288','12289','12290','12291','12292','12293','12294','12295','12296','12297','12298','12299','12300','12301','12302','12303','12304','12305','12306','12307','12309','12310','12311','12312','12313','12314','12315','12316','12317','12318','12319','12320','12321','12322','12323','12324','12325','12326','12327','12328','12329','12331','12332']),
  deployment_6: new Set(['12336','12337','12338','12339','12341','12342','12343','12345','12346','12347','12348','12349','12350','12351','12352','12353','12354','12355','12356','12357','12358','12359','12360','12361','12362','12363','12364','12365','12366','12367','12368','12369','12370','12371']),
  deployment_7: new Set(['12311','12354','12375','12376','12377','12378','12379','12380','12381','12382','12383','12384','12385','12386','12387','12389','12390','12391','12392','12393','12394','12395','12396','12397','12398','12399','12400','12401','12402','12403','12404','12405','12406','12407','12408','12410','12411','12412','12413','12414','12415','12416','12417','12418','12419','12420','12421','12422','12423','12424','12425','12426','12427','12428','12429','12430','12431','12432','12433','12434','12435','12436','12437','12438','12439','12440','12442','12443','12444','12445','12446','12447','12448','12449','12450','12451','12452','12453','12454','12455','12456','12457','12458','12459','12460','12461','12462','12463','12464','12465','12466','12467']),
  deployment_8: new Set(['12467','12472','12473','12474','12475','12476','12477','12478','12479','12480','12481','12482','12483','12484','12485','12486','12487','12488','12489','12490','12491','12492','12493','12494','12495','12496','12497','12498','12499','12500','12501','12502','12503','12504','12505']),
  deployment_13: new Set(['12044','12045','12046','12047','12048','12049','12050','12051','12053','12054','12055','12056','12057','12058','12059','12060','12061','12063','12064','12065','12066','12067','12068','12069','12070','12071','12072','12073','12074','12075','12076','12077','12078','12079','12080','12081','12082','12083','12084','12085','12086','12087','12088','12089','12090','12091','12092','12093','12094','12095','12096','12097','12098','12099','12100','12101','12102','12104','12105','12106','12107','12108','12109','12110','12111','12112','12113','12114','12115','12116','12117','12118','12119','12120','12121','12122','12123','12124','12125','12128','12129','12130','12133','12134','12136','12137','12138','12139','12140','12141','12142','12143','12144','12145','12146','12147','12148','12149','12150','12151','12152','12153','12155','12156','12157','12159','12160']),
}

// ─── About Page (RICO-style) ──────────────────────────────────────────────────

// ─── About Page (Tailwind) ─────────────────────────────────────────────────

// ─── About Page (RICO-style) ──────────────────────────────────────────────────

function AboutPage({ onExplore }) {
  const isMobile = useIsMobile()
  const [copiedCitation, setCopiedCitation] = useState(false)

  // Transcribed from the paper's own ACM Reference Format block (p.1 of the PDF in
  // public/paper). The previous value here named a different venue, a different title
  // and two of the seven authors — it was never rendered, so nothing surfaced the drift.
  const citation = `@inproceedings{barron2026tutortrace,
  title={TutorTrace: A Dataset and Taxonomy for Classifying Learner Behavioral States during AI-Assisted Programming Education},
  author={Barron, David and Tang, Xiaohang and Dwisantika, Rezky and Kim, Minsun and Smith IV, David H. and Cui, Jiaming and Chen, Yan},
  booktitle={Proceedings of the 39th Annual ACM Symposium on User Interface Software and Technology (UIST '26)},
  year={2026},
  publisher={ACM},
  address={New York, NY, USA},
  doi={10.1145/3830398.3830712}
}`

  const PAPER_PDF = '/paper/_UIST_26__TutorTrace__A_Dataset_and_Taxonomy_for_Classifying_Learner_Behavioral_States_during_AI_Assisted_Programming_Education.pdf'
  const PAPER_THUMB = '/paper/tutortrace-paper-thumb.png'
  const GITHUB_URL = 'https://github.com/divadbaroon/TutorTrace_dataset_and_benchmark'

  const copyCitation = () => {
    navigator.clipboard.writeText(citation)
    setCopiedCitation(true)
    setTimeout(() => setCopiedCitation(false), 2000)
  }

  const S = {
    page: { flex: 1, overflowY: 'auto', backgroundColor: '#fff' },
    inner: { maxWidth: '640px', margin: '0 auto', padding: isMobile ? '28px 18px 60px' : '48px 32px 80px' },
    title: { fontSize: isMobile ? '3rem' : '6rem', fontWeight: 900, color: '#111', margin: isMobile ? '0 0 24px' : '0 0 32px', letterSpacing: '-0.05em', lineHeight: 1, textAlign: 'center' },
    p: { fontSize: '1rem', color: '#333', lineHeight: 1.75, margin: '0 0 1.25em', textAlign: isMobile ? 'left' : 'justify' },
    h3: { fontSize: '1.25em', fontWeight: 600, color: '#111', margin: '2em 0 0.6em', lineHeight: 1.6 },
    card: { borderRadius: '12px', border: '1px solid #e5e5e5', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: '24px', background: '#fff' },
    code: { display: 'block', background: '#0d1117', color: '#c9d1d9', padding: '16px 20px', borderRadius: '6px', fontSize: '0.875em', fontFamily: "'Geist Mono', 'JetBrains Mono', monospace", lineHeight: 1.7, overflowX: 'auto', whiteSpace: 'pre', margin: '1.7em 0' },
    ic: { fontSize: '0.875em', fontWeight: 600, color: '#111', fontFamily: "'Geist Mono', monospace" },
    tableWrap: { overflowX: 'auto', margin: '2em 0', WebkitOverflowScrolling: 'touch' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.875em', lineHeight: 1.7, minWidth: isMobile ? '480px' : 'auto' },
    th: { textAlign: 'left', padding: '0 10px 10px', borderBottom: '1px solid #d4d4d4', color: '#111', fontWeight: 600 },
    td: { padding: '10px', borderBottom: '1px solid #e5e5e5', color: '#333' },
    btn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '8px 16px', borderRadius: '6px', border: 'none', background: '#171717', color: '#fafafa', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer', transition: 'background 0.15s', textDecoration: 'none' },
    ghost: { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '6px', border: '1px solid #e5e5e5', background: '#fff', fontSize: '0.75rem', fontWeight: 500, color: '#555', cursor: 'pointer' },
    ol: { margin: '12px 0', paddingLeft: '24px', listStyleType: 'decimal' },
    li: { fontSize: '1rem', color: '#333', lineHeight: 1.75, marginBottom: '8px' },
    link: { color: '#111', fontWeight: 500, textDecoration: 'underline' },
    smallLink: { color: '#111', fontWeight: 500, textDecoration: 'underline', fontSize: '0.875rem' },
    muted: { fontSize: '0.875rem', color: '#737373' },
    ethics: { padding: '20px 24px', borderRadius: '6px', border: '1px solid #bbf7d0', background: '#f0fdf4', margin: '1.25em 0' },
    wideGrid: { display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '24px', margin: '2em 0' },
    // Rico's proportions: cover column to citation column is about 1:2, with the
    // download link sitting under the cover rather than beside it. Stacks on mobile.
    paperRow: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr', gap: isMobile ? '22px' : '32px', alignItems: 'start', margin: '1.5em 0 2.5em' },
    paperThumbLink: { display: 'block', lineHeight: 0 },
    paperThumb: { width: '100%', height: 'auto', border: '1px solid #e5e5e5', borderRadius: '10px', display: 'block', background: '#fff' },
    paperDownload: { display: 'inline-block', marginTop: '14px', fontSize: '1rem', fontWeight: 500, color: '#111', textDecoration: 'underline', textUnderlineOffset: '3px' },
    // The citation sits in its own rounded panel on Rico. That page is dark; this one
    // is white, so the panel is a light tint rather than a copy of its colours — a
    // single dark card on a white page would read as a rendering fault, not a style.
    paperCard: { background: '#fafafa', border: '1px solid #ececec', borderRadius: '20px', padding: isMobile ? '22px 20px' : '28px 30px' },
    paperCite: { fontSize: '0.9375rem', lineHeight: 1.65, color: '#333', margin: 0, textAlign: 'left' },
    // `inline`, not inline-block: it sits mid-sentence and must share the paragraph's
    // baseline and line box, or it drops below the last line of the citation.
    paperBibtex: { display: 'inline', margin: 0, padding: 0, background: 'none', border: 0, cursor: 'pointer', fontSize: 'inherit', lineHeight: 'inherit', fontWeight: 500, color: '#111', textDecoration: 'underline', textUnderlineOffset: '3px', fontFamily: 'inherit' },
  }

  return (
    <div style={S.page}>
      <div style={S.inner}>

        {/* ── Title ── */}
        <h1 style={S.title}>TutorTrace</h1>

        {/* ── Download Card ── */}
        <div style={S.card}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '1.5rem', fontWeight: 600, color: '#111' }}>TutorTrace on GitHub</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <a href={GITHUB_URL} target="_blank" rel="noopener noreferrer"
                style={S.btn}
                onMouseEnter={e => e.currentTarget.style.background = '#171717e6'}
                onMouseLeave={e => e.currentTarget.style.background = '#171717'}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="#fafafa"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/></svg>
                View repository
              </a>
            </div>
          </div>
        </div>

        {/* ── Paper ── */}
        {/* Laid out to match Rico's archive page: cover left with the download link
            directly beneath it, citation card to the right at roughly a 1:2 ratio.
            The download is a plain underlined link there, not a filled button — it
            reads as part of the cover rather than as a call to action. */}
        <h3 style={{ ...S.h3, marginTop: '2.5em' }}>Paper</h3>
        <div style={S.paperRow}>
          <div>
            <a href={PAPER_PDF} target="_blank" rel="noopener noreferrer" style={S.paperThumbLink}>
              <img src={PAPER_THUMB} alt="First page of the TutorTrace paper" style={S.paperThumb} />
            </a>
            <a href={PAPER_PDF} target="_blank" rel="noopener noreferrer" style={S.paperDownload}>
              Download (4.6MB) ↓
            </a>
          </div>

          <div style={S.paperCard}>
            <p style={S.paperCite}>
              David Barron, Xiaohang Tang, Rezky Dwisantika, Minsun Kim, David H. Smith IV,
              Jiaming Cui and Yan Chen. 2026.{' '}
              <strong style={{ fontWeight: 700, color: '#111' }}>
                TutorTrace: A Dataset and Taxonomy for Classifying Learner Behavioral
                States during AI-Assisted Programming Education
              </strong>. In Proceedings of the 39th{' '}
              <em>Annual ACM Symposium on User Interface Software and Technology (UIST 2026)</em>.{' '}
              {/* Rico links out to a .txt; copying to the clipboard is the same gesture
                  in one step, so the label stays "Bibtex" and the styling stays a link.
                  Inline at the end of the citation rather than on its own line. */}
              <button onClick={copyCitation} style={S.paperBibtex}>
                {copiedCitation ? 'Copied ✓' : 'Bibtex'}
              </button>
            </p>
          </div>
        </div>

        {/* ── Abstract ── */}
        <p style={S.p}>
          <strong style={{ color: '#111' }}>This paper presents TutorTrace, a behavioral telemetry dataset capturing fine-grained IDE interactions from 664 novice programmers</strong> working with an integrated AI coding tutor across 8 classroom deployments of introductory programming courses. The dataset enables prediction of behavioral states, error imminence, query imminence, and help-seeking quality from observable coding patterns. To demonstrate the kinds of applications that TutorTrace enables, we present results from four prediction benchmarks trained on deployment D1 and evaluated on D2, achieving AUCs of .828 (next state), .868 (error imminence), .843 (query imminence), and .771 (query type).
        </p>

        {/* ── The Dataset ── */}
        <h3 style={S.h3}>The Dataset</h3>
        <p style={S.p}>
          We collected telemetry from 664 students across 8 classroom deployments in Spring 2026 introductory programming courses at Virginia Tech. Students used VizPI, a web-based coding environment with an integrated GPT-4o tutor, to solve timed programming tasks. The TutorTrace dataset contains 882,367 telemetry events capturing code edits, terminal runs, errors, AI queries and responses, test results, and contextual signals across more than 664 unique coding sessions.
        </p>

        {/* ── Data Collection ── */}
        <h3 style={S.h3}>Data Collection</h3>
        <p style={S.p}>
          TutorTrace was built by capturing fine-grained IDE interactions at runtime as students worked in VizPI during regular class sessions. The environment presented a programming task, a code editor, a terminal for running and testing code, and an optional AI chat panel. All interactions were recorded as timestamped telemetry events in real time — no modification of the student's workflow was required.
        </p>
        <p style={S.p}>
          The AI tutor was powered by GPT-4o with a pedagogical system prompt designed to guide rather than give direct answers. Students could choose whether or not to use the AI assistant — across all deployments, 58% of students (386) used it at least once, generating 1,692 labeled queries. After collecting raw telemetry, we ran an automated behavioral classifier to segment each session into implementing, debugging, testing, seeking help, and thinking states. In total, the dataset comprises 15,991 behavioral segments.
        </p>

        {/* ── Data Representation ── */}
        <h3 style={S.h3}>Data Representation</h3>
        <p style={S.p}>
          For each deployment, TutorTrace exposes three layers of data. <strong>Raw telemetry</strong> captures every IDE event with timestamps, event types, and payloads. <strong>Observable metrics</strong> provide 87,439 window-level observations (30s window, 5s step) with derived features like edit rates, query rates, and error self-fix patterns, plus 1,576 query-level features capturing pre-query behavioral context. <strong>Behavioral sequences</strong> contain 15,991 auto-segmented states with durations and thinking subtypes.
        </p>
        <p style={S.p}>
          Each event exposes its <span style={S.ic}>type</span> (one of 30+ event types including <span style={S.ic}>CODE_TYPE</span>, <span style={S.ic}>TERMINAL_RUN</span>, <span style={S.ic}>CHAT_QUERY</span>, <span style={S.ic}>TEST_CASE_RESULT</span>), a <span style={S.ic}>timestamp</span> in milliseconds, and a <span style={S.ic}>payload</span> containing event-specific data. Window-level features include imminence labels at multiple horizons (5s, 10s, 15s, 30s, 45s, 60s) for both errors and queries.
        </p>

        {/* ── Benchmark ── */}
        <h3 style={S.h3}>Prediction Benchmark</h3>
        <p style={S.p}>
          The TutorTrace dataset is large enough to support training behavioral prediction models. We present four benchmark tasks trained on D1 and evaluated on D2. All LLMs tested (GPT-4o, Llama 3.1, Qwen 3.5, DeepSeek-R1) perform near chance, indicating behavioral prediction requires task-specific training on telemetry data.
        </p>
        <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Task</th>
              <th style={S.th}>Type</th>
              <th style={S.th}>Best Model</th>
              <th style={S.th}>AUC</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Next Behavioral State', '5-class', 'XGB+Seq-CNN', '.828'],
              ['Error Imminence (15s)', 'Binary', 'XGB+Seq-Trans', '.868'],
              ['Query Imminence (15s)', 'Binary', 'XGB+Seq-Trans', '.843'],
              ['Query Type', 'Binary', 'Seq-GRU', '.771'],
            ].map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => <td key={j} style={{ ...S.td, fontWeight: j === 0 ? 600 : 400 }}>{cell}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        {/* ── Quick Downloads Card ── */}
        <div style={{ ...S.card, marginTop: '2em' }}>
          <div style={{ fontWeight: 600, fontSize: '1rem', color: '#111', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>Quick Downloads</div>
          <ol style={S.ol}>
            <li style={S.li}><a href="#raw-telemetry" style={S.link}>Raw Telemetry — JSON event streams per deployment</a></li>
            <li style={S.li}><a href="#behavioral-sequences" style={S.link}>Behavioral Sequences — auto-segmented states (CSV)</a></li>
            <li style={S.li}><a href="#window-features" style={S.link}>Window-Level Features — 30s sliding window observations (CSV)</a></li>
            <li style={S.li}><a href="#query-features" style={S.link}>Query-Level Features — per-query behavioral context (CSV)</a></li>
            <li style={S.li}><a href="#query-labels" style={S.link}>Query Labels — guided/dependent classifications (CSV)</a></li>
            <li style={S.li}><a href="#demographics" style={S.link}>Demographics & Post-Surveys — de-identified (CSV)</a></li>
          </ol>
          <div style={{ display: 'flex', alignItems: 'center', padding: '12px 0 0', borderTop: '1px solid #f0f0f0', marginTop: '8px' }}>
            <a href="https://github.com/divadbaroon/TutorTrace_dataset_and_benchmark" target="_blank" rel="noopener noreferrer" style={{ ...S.smallLink, textDecoration: 'underline' }}>
              Full Repository ↗
            </a>
          </div>
        </div>

        {/* ── Deployments Table ── */}
        <h3 style={S.h3}>Deployments</h3>
        <p style={S.p}>
          The dataset spans 8 classroom deployments across two courses, three instructors, and two programming languages. D1 serves as the training split; all others are test splits for cross-deployment evaluation.
        </p>
        <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>ID</th>
              <th style={S.th}>Split</th>
              <th style={S.th}>Task</th>
              <th style={S.th}>Lang</th>
              <th style={S.th}>Students</th>
              <th style={S.th}>AI Users</th>
              <th style={S.th}>Queries</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['D1', 'Train', 'GradeBook', 'Py', '190', '94', '428'],
              ['D2', 'Test', 'GradeBook', 'Py', '113', '90', '536'],
              ['D3', 'Test', 'Playlist', 'Py', '70', '49', '190'],
              ['D4', 'Test', 'Rectangle', 'Py', '49', '48', '190'],
              ['D5', 'Test', 'Rectangle', 'Py', '80', '37', '99'],
              ['D6', 'Test', 'GradeScore', 'C', '35', '25', '120'],
              ['D7', 'Test', 'Rectangle', 'Py', '92', '43', '202'],
              ['D8', 'Test', 'Rectangle', 'Py', '35', '15', '59'],
            ].map(row => (
              <tr key={row[0]}>
                {row.map((cell, i) => <td key={i} style={{ ...S.td, fontWeight: i === 0 ? 600 : 400 }}>{cell}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        {/* ── 1. Raw Telemetry ── */}
        <h3 id="raw-telemetry" style={S.h3}>1. Raw Telemetry</h3>
        <p style={S.p}>
          <a href="https://github.com/divadbaroon/TutorTrace_dataset_and_benchmark" target="_blank" rel="noopener noreferrer" style={S.link}>Download ↓</a>
        </p>
        <p style={S.p}>
          Contains 882,367 events from 664 students across 8 deployments. For each deployment, we provide a JSON file keyed by student ID. Each student entry contains a <span style={S.ic}>student_name</span> (anonymized), <span style={S.ic}>event_count</span>, and a chronologically ordered <span style={S.ic}>events</span> array. A sample entry is shown below:
        </p>
        <pre style={S.code}>{`{
  "11690": {
    "student_name": "Guest_x7k2m1 User",
    "event_count": 847,
    "events": [
      {"timestamp": 1772119812650, "type": "SESSION_START",
       "payload": {"window_width": 1536, "window_height": 730}},
      {"timestamp": 1772119815052, "type": "CODE_TYPE",
       "payload": {"text": "def grade_book(", "length": 15}},
      {"timestamp": 1772119891234, "type": "TERMINAL_RUN",
       "payload": {"code_length": 342}},
      {"timestamp": 1772119923456, "type": "CHAT_QUERY",
       "payload": {"text": "How do I handle the edge case?"}},
      {"timestamp": 1772119925678, "type": "CHAT_RESPONSE",
       "payload": {"text": "Consider what happens when..."}},
      {"timestamp": 1772119945678, "type": "TEST_CASE_RESULT",
       "payload": {"passed_count": 3, "total_tests": 5}}
    ]
  }
}`}</pre>

        {/* ── 2. Behavioral Sequences ── */}
        <h3 id="behavioral-sequences" style={S.h3}>2. Behavioral Sequences</h3>
        <p style={S.p}>
          <a href="https://github.com/divadbaroon/TutorTrace_dataset_and_benchmark" target="_blank" rel="noopener noreferrer" style={S.link}>Download ↓</a>
        </p>
        <p style={S.p}>
          Contains 15,991 auto-segmented behavioral states. Each segment includes a <span style={S.ic}>student_id</span>, <span style={S.ic}>behavioral_state</span> (one of: implementing, debugging, testing, seekingHelp, thinking), start/end times, and duration. Thinking segments include a <span style={S.ic}>thinking_subtype</span> field (thinking-task, thinking-llm, thinking-error, thinking-code).
        </p>
        <pre style={S.code}>{`student_id,segment_index,behavioral_state,thinking_subtype,start_time_ms,end_time_ms,duration_s
11690,0,thinking,thinking-task,0,12450,12.45
11690,1,implementing,,12450,45230,32.78
11690,2,debugging,,45230,62100,16.87
11690,3,seekingHelp,,62100,78900,16.80
11690,4,implementing,,78900,95400,16.50`}</pre>

        {/* ── 3. Window-Level Features ── */}
        <h3 id="window-features" style={S.h3}>3. Window-Level Features</h3>
        <p style={S.p}>
          <a href="https://github.com/divadbaroon/TutorTrace_dataset_and_benchmark" target="_blank" rel="noopener noreferrer" style={S.link}>Download ↓</a>
        </p>
        <p style={S.p}>
          Contains 87,439 observations computed from 30-second sliding windows with a 5-second step. Each row includes three abstraction layers: raw event counts (<span style={S.ic}>code_events</span>, <span style={S.ic}>terminal_runs</span>, <span style={S.ic}>terminal_errors</span>), derived observable metrics (<span style={S.ic}>cum_code_rate</span>, <span style={S.ic}>delete_ratio</span>, <span style={S.ic}>error_self_fix</span>, <span style={S.ic}>prior_no_effort_rate</span>), and behavioral sequence features (<span style={S.ic}>current_state</span>, <span style={S.ic}>pct_implementing</span>). Each row also contains imminence labels at horizons of 5s, 10s, 15s, 30s, 45s, and 60s.
        </p>

        {/* ── 4. Query-Level Features ── */}
        <h3 id="query-features" style={S.h3}>4. Query-Level Features</h3>
        <p style={S.p}>
          <a href="https://github.com/divadbaroon/TutorTrace_dataset_and_benchmark" target="_blank" rel="noopener noreferrer" style={S.link}>Download ↓</a>
        </p>
        <p style={S.p}>
          Contains 1,576 per-query feature vectors capturing the behavioral context between the last AI response and 15 seconds before the next query. Features include <span style={S.ic}>pre_code_edit_rate</span>, <span style={S.ic}>pre_terminal_runs</span>, <span style={S.ic}>pre_error_self_fix</span>, <span style={S.ic}>pre_chat_to_code_latency_s</span>, and behavioral time breakdowns (<span style={S.ic}>implementing_time_s</span>, <span style={S.ic}>debugging_time_s</span>, etc.).
        </p>

        {/* ── 5. Query Labels ── */}
        <h3 id="query-labels" style={S.h3}>5. Query Labels</h3>
        <p style={S.p}>
          <a href="https://github.com/divadbaroon/TutorTrace_dataset_and_benchmark" target="_blank" rel="noopener noreferrer" style={S.link}>Download ↓</a>
        </p>
        <p style={S.p}>
          Contains 1,692 queries labeled as <em>guided</em> (productive help-seeking) or <em>dependent</em> (outsourcing). Labels were generated by GPT-4o and validated against human annotators (κ = .897 human–human, κ = .709/.690 GPT–human).
        </p>

        {/* ── 6. Demographics & Post-Surveys ── */}
        <h3 id="demographics" style={S.h3}>6. Demographics & Post-Surveys</h3>
        <p style={S.p}>
          <a href="https://github.com/divadbaroon/TutorTrace_dataset_and_benchmark" target="_blank" rel="noopener noreferrer" style={S.link}>Download ↓</a>
        </p>
        <p style={S.p}>
          Contains de-identified demographic information and post-session self-reported understanding surveys. All personally identifiable information has been removed.
        </p>

        {/* ── Quick Start ── */}
        <h3 style={S.h3}>Quick Start</h3>
        <pre style={S.code}>{`# Install dependencies
pip install pandas numpy scikit-learn xgboost torch pyyaml

# Prepare derived datasets from raw telemetry
python prepare_data.py --force

# Run the benchmark (D1 → D2)
cd benchmark
python run_benchmark.py

# Cross-deployment evaluation
python benchmark/run_all_benchmark.py`}</pre>

        {/* ── Ethics ── */}
        <h3 style={S.h3}>Ethics</h3>
        <div style={S.card}>
          <p style={{ ...S.p, margin: 0 }}>
            All data is de-identified with randomized IDs. No personally identifiable information is included. Query text content is not included in released features. The study was approved under the university's IRB. Students consented to data collection and were informed that participation was voluntary and would not affect their grades.
          </p>
        </div>

        {/* ── Explore CTA ── */}
        <div style={{ textAlign: 'center', marginTop: '3em' }}>
          <button onClick={onExplore} style={{ ...S.btn, padding: '10px 24px', fontSize: '1rem' }}
            onMouseEnter={e => e.currentTarget.style.background = '#333'}
            onMouseLeave={e => e.currentTarget.style.background = '#171717'}
          >
            Explore the Dataset Online →
          </button>
        </div>

      </div>
    </div>
  )
}



// ─── Applications Page ────────────────────────────────────────────────────────

function ApplicationsPage() {
  const isMobile = useIsMobile()
  const S = {
    page: { flex: 1, overflowY: 'auto', backgroundColor: '#fff' },
    inner: { maxWidth: '960px', margin: '0 auto', padding: isMobile ? '32px 18px 60px' : '60px 40px 80px' },
    title: { fontSize: isMobile ? '28px' : '36px', fontWeight: 800, color: '#111', margin: '0 0 12px', letterSpacing: '-0.03em', lineHeight: 1.15, textAlign: 'center' },
    divider: { width: '60px', height: '3px', background: '#ef4444', borderRadius: '2px', margin: '16px auto 40px' },
    section: { marginBottom: isMobile ? '40px' : '56px' },
    sectionTitle: { fontSize: isMobile ? '20px' : '24px', fontWeight: 800, color: '#111', margin: '0 0 8px', letterSpacing: '-0.02em', textAlign: 'center' },
    sectionDivider: { width: '40px', height: '3px', background: '#3b82f6', borderRadius: '2px', margin: '12px auto 32px' },
    grid: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: isMobile ? '14px' : '24px' },
    gridTwo: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '24px', maxWidth: '680px', margin: '0 auto' },
  }

  const AppCard = ({ title, description }) => (
    <div style={{
      padding: '28px 24px', borderRadius: '12px', border: '1.5px solid #e8e8eb',
      background: '#fff', textAlign: 'center', transition: 'box-shadow 0.2s, border-color 0.2s',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = '#ccc'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(0,0,0,0.07)' }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = '#e8e8eb'; e.currentTarget.style.boxShadow = 'none' }}
    >
      <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#111', margin: '0 0 10px', lineHeight: 1.3 }}>{title}</h3>
      <p style={{ fontSize: '13px', color: '#666', lineHeight: 1.6, margin: 0 }}>{description}</p>
    </div>
  )

  return (
    <div style={S.page}>
      <div style={S.inner}>
        <h1 style={S.title}>Applications</h1>
        <div style={S.divider} />

        {/* Benchmark */}
        <div style={S.section}>
          <h2 style={S.sectionTitle}>Prediction Benchmark</h2>
          <div style={S.sectionDivider} />
          <p style={{ fontSize: '15px', color: '#555', lineHeight: 1.7, margin: '0 auto 28px', maxWidth: '700px', textAlign: 'center' }}>
            Four prediction tasks trained on D1, evaluated on D2. All LLMs tested perform near chance — behavioral prediction requires task-specific training.
          </p>

          <div style={S.grid}>
            <AppCard
              title="Next Behavioral State"
              description="Predict what a student will do next — implementing, debugging, testing, seeking help, or thinking — from a 30-second window. Best: XGB+Seq-CNN."
            />
            <AppCard
              title="Error Imminence"
              description="Will the student hit a terminal error within 15 seconds? Detect impending errors from behavioral patterns. Best: XGB+Seq-Transformer."
            />
            <AppCard
              title="Query Imminence"
              description="Will the student ask the AI for help within 15 seconds? Predict help-seeking from coding patterns. Best: XGB+Seq-Transformer."
            />
            <AppCard
              title="Query Type"
              description="Is the query guided (productive) or dependent (outsourcing)? Classify from behavioral context before the query is sent. Best: Seq-GRU."
            />
          </div>
        </div>

        {/* Beyond Benchmarks */}
        <div style={S.section}>
          <h2 style={S.sectionTitle}>Beyond Benchmarks</h2>
          <div style={S.sectionDivider} />

          <div style={S.grid}>
            <AppCard
              title="Behavioral Awareness for Instructors"
              description="Real-time dashboards showing which students are struggling, outsourcing to AI, or productively engaged — enabling timely intervention."
            />
            <AppCard
              title="Help-Seeking Quality Assessment"
              description="What patterns precede productive help-seeking vs. passive outsourcing? Guided vs. dependent labels inform adaptive AI tutoring design."
            />
            <AppCard
              title="Process-Based Assessment"
              description="Evaluate how students approach problems, respond to errors, and integrate AI — not just final code output."
            />
            <AppCard
              title="Adaptive Struggle Detection"
              description="Classify struggle as productive, unproductive, or avoided — informing when to intervene and when to let students work through challenges."
            />
            <AppCard
              title="Custody of Verification"
              description="Detect whether students maintain ownership of verifying AI-generated code through behavioral patterns — a key signal for independent competence."
            />
            <AppCard
              title="Cross-Deployment Generalization"
              description="Evaluate how behavioral models transfer across different tasks, instructors, and programming languages using 8 diverse deployments."
            />
          </div>
        </div>
      </div>
    </div>
  )
}


// ─── Main Page ────────────────────────────────────────────────────────────────

const MODES = [
  { key: 'about', label: 'About', icon: AboutIcon, ready: true },
  { key: 'datasets', label: 'Datasets', icon: DatasetIcon, ready: true },
  { key: 'applications', label: 'Applications', icon: ApplicationsIcon, ready: true },
]

export default function DatasetPage() {
  const isMobile = useIsMobile()
  const [mode, setMode] = useState('about')
  const [selectedDeployment, setSelectedDeployment] = useState(null)
  const [deploymentSearch, setDeploymentSearch] = useState('')
  const [courseFilter, setCourseFilter] = useState('all')
  const [deploymentSort, setDeploymentSort] = useState('students')

  const [deploymentData, setDeploymentData] = useState({})
  const loadingRef = useRef(new Set())

  // ── Load deployment data (cache-first) ──────────────────────────────────

  const loadDeployment = useCallback(async (deployment) => {
    const did = deployment.id
    if (loadingRef.current.has(did)) return
    loadingRef.current.add(did)

    const cached = await getCachedDeployment(did)
    if (cached) {
      setDeploymentData(prev => prev[did]?.eventsByUser ? prev : { ...prev, [did]: cached })
      return
    }

    setDeploymentData(prev => {
      if (prev[did]?.eventsByUser) return prev
      return { ...prev, [did]: { ...prev[did], loading: true, error: null } }
    })

    try {
      const [rawData, users, session] = await Promise.all([
        getTelemetryEventsBySession(deployment.sessionId),
        getAllUsersInSession(deployment.sessionId).catch(() => []),
        getSessionById(deployment.sessionId).catch(() => null),
      ])

      const filtered = {}
      for (const [userId, events] of Object.entries(rawData)) {
        const inRange = events.filter(
          e => e.timestamp >= deployment.window.start && e.timestamp <= deployment.window.end
        )
        if (inRange.length > 0) filtered[userId] = inRange
      }

      const analyticsById = {}
      for (const [userId, events] of Object.entries(filtered)) {
        if (!events || events.length === 0) continue
        const first = events[0]?.timestamp
        const last = events[events.length - 1]?.timestamp
        if (!first || !last || (last - first) < 15000) continue
        if (!events.some(e => e.type !== 'SESSION_START')) continue

        const conversation = events
          .filter(e => e.type === 'CHAT_QUERY' || e.type === 'CHAT_RESPONSE')
          .map(e => ({
            role: e.type === 'CHAT_QUERY' ? 'user' : 'assistant',
            content: e.payload?.text || e.payload?.message || e.payload?.content || '',
            timestamp: e.timestamp,
            intent: e.payload?.intent || null,
          }))
          .sort((a, b) => a.timestamp - b.timestamp)

        analyticsById[userId] = {
          analytics: computeStudentAnalytics({ rawEvents: events, conversation, autoSegmentEvents }),
          conversation,
          rawEvents: events,
        }
      }

      const validUserIds = new Set(Object.keys(filtered))
      const studentsWithData = users
        .filter(u => validUserIds.has(String(u.id)))
        .map(u => {
          const a = analyticsById[u.id]?.analytics || analyticsById[String(u.id)]?.analytics
          const stats = a?.sessionStats?.full
          return {
            ...u,
            duration: a?.replayDuration ? Math.round(a.replayDuration / 1000) : null,
            chat_count: stats?.frequency?.chatQuery?.count ?? 0,
            tests_passed: stats?.testResults?.lastPassedCount ?? null,
            tests_total: stats?.testResults?.lastTotalTests ?? null,
          }
        })

      const allowlist = DATASET_ALLOWLIST[did]
      const finalStudents = (studentsWithData.length > 0
        ? studentsWithData
        : Object.keys(filtered).map(uid => {
            const a = analyticsById[uid]?.analytics
            const stats = a?.sessionStats?.full
            return {
              id: uid,
              display_name: `Student ${uid.slice(-4)}`,
              duration: a?.replayDuration ? Math.round(a.replayDuration / 1000) : null,
              chat_count: stats?.frequency?.chatQuery?.count ?? 0,
              tests_passed: stats?.testResults?.lastPassedCount ?? null,
              tests_total: stats?.testResults?.lastTotalTests ?? null,
            }
          })
      ).filter(s => !allowlist || allowlist.has(String(s.id)))

      // Keep eventsByUser in sync with the allowlist-filtered student list
      const finalStudentIds = new Set(finalStudents.map(s => String(s.id)))
      const finalEventsByUser = Object.fromEntries(
        Object.entries(filtered).filter(([uid]) => finalStudentIds.has(String(uid)))
      )

      const finalData = {
        eventsByUser: finalEventsByUser,
        analyticsById,
        students: finalStudents,
        session,
        loading: false,
        error: null,
        totalEvents: Object.values(finalEventsByUser).flat().length,
      }

      setDeploymentData(prev => ({ ...prev, [did]: finalData }))
      setCachedDeployment(did, finalData)
    } catch (err) {
      console.error(`Error loading deployment ${did}:`, err)
      setDeploymentData(prev => ({
        ...prev,
        [did]: { ...prev[did], loading: false, error: err.message },
      }))
    }
  }, [])

  useEffect(() => {
    DEPLOYMENTS.forEach(dep => loadDeployment(dep))
  }, [])

  const handleSelectDeployment = (deployment) => {
    loadDeployment(deployment)
    setSelectedDeployment(deployment)
  }

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && selectedDeployment) {
        setSelectedDeployment(null)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedDeployment])

  const handleDownloadDeployment = (deployment) => {
    const data = deploymentData[deployment.id]
    if (!data?.eventsByUser) return

    const output = {
      deployment: deployment.id,
      session_id: deployment.sessionId,
      section_id: deployment.sectionId,
      course: deployment.course,
      language: deployment.language,
      date: deployment.date,
      time: deployment.time,
      student_count: Object.keys(data.eventsByUser).length,
      total_events: Object.values(data.eventsByUser).flat().length,
      students: Object.entries(data.eventsByUser).map(([userId, events]) => {
        const student = (data.students || []).find(s => String(s.id) === String(userId))
        return {
          student_id: userId,
          name: student?.display_name || student?.username || `Student ${userId}`,
          event_count: events.length,
          events,
        }
      }),
    }

    const blob = new Blob([JSON.stringify(output, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `telemetry_${deployment.id}_all_students.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const deploymentSummaries = useMemo(() => {
    const summaries = {}
    for (const dep of DEPLOYMENTS) {
      const data = deploymentData[dep.id]
      if (data?.students) {
        const s = data.students
        const passed = s.filter(st => st.tests_passed === st.tests_total && st.tests_total > 0).length
        const aiUsers = s.filter(st => st.chat_count > 0).length
        const avgDur = s.length > 0 ? Math.round(s.reduce((sum, st) => sum + (st.duration || 0), 0) / s.length) : 0
        summaries[dep.id] = {
          studentCount: s.length,
          totalEvents: data.totalEvents || 0,
          passRate: s.length > 0 ? Math.round((passed / s.length) * 100) : 0,
          aiUsage: s.length > 0 ? Math.round((aiUsers / s.length) * 100) : 0,
          avgDuration: avgDur,
          loaded: true,
        }
      }
    }
    return summaries
  }, [deploymentData])

  const caseStudyData = useMemo(() => {
    const findById = (studentId) => {
      for (const dep of DEPLOYMENTS) {
        const data = deploymentData[dep.id]
        if (!data?.analyticsById) continue
        const entry = data.analyticsById[studentId] || data.analyticsById[String(studentId)]
        if (!entry?.analytics) continue
        const a = entry.analytics
        const beforeStats = a.sessionStats?.before
        const firstQuery = (a.filteredEvents || []).find(e => e.type === 'CHAT_QUERY')
        const firstQueryText = firstQuery?.payload?.text || firstQuery?.payload?.message || firstQuery?.payload?.content || ''
        return {
          id: studentId,
          segments: a.beforeSegments || [],
          duration: a.firstQueryMs || 0,
          stats: {
            edits: beforeStats?.frequency?.codeEdit?.count ?? 0,
            charsDeleted: beforeStats?.codeMetrics?.totalDeleted ?? 0,
            runs: beforeStats?.frequency?.terminalRun?.count ?? 0,
            netCodeGrowth: beforeStats?.codeMetrics?.netCodeGrowth ?? 0,
          },
          firstQueryText,
          runTimes: (a.filteredEvents || [])
            .filter(e => e.type === 'TERMINAL_RUN' && (e.timestamp - a.replayStartTime) < (a.firstQueryMs || 0))
            .map(e => e.timestamp - a.replayStartTime),
        }
      }
      return null
    }
    return { studentA: findById(CASE_STUDY.a.id), studentB: findById(CASE_STUDY.b.id) }
  }, [deploymentData])

  const COURSE_DISPLAY = {
    Python: { name: 'Intro to Programming in Python', code: 'CS 1064' },
    C:      { name: 'Introduction to Programming in C', code: 'CS 1044' },
  }
  const uniqueLanguages = useMemo(() => [...new Set(DEPLOYMENTS.map(d => d.language))].sort(), [])

  const filteredDeployments = useMemo(() => {
    let result = [...DEPLOYMENTS]
    if (deploymentSearch.trim()) {
      const q = deploymentSearch.toLowerCase()
      result = result.filter(d =>
        d.course?.toLowerCase().includes(q) || d.label?.toLowerCase().includes(q) ||
        d.date?.toLowerCase().includes(q) || d.language?.toLowerCase().includes(q) ||
        (COURSE_DISPLAY[d.language]?.name || '').toLowerCase().includes(q) ||
        (COURSE_DISPLAY[d.language]?.code || '').toLowerCase().includes(q)
      )
    }
    if (courseFilter !== 'all') result = result.filter(d => d.language === courseFilter)
    result.sort((a, b) => {
      const sa = deploymentSummaries[a.id], sb = deploymentSummaries[b.id]
      if (deploymentSort === 'students') return (sb?.studentCount ?? 0) - (sa?.studentCount ?? 0)
      if (deploymentSort === 'events') return (sb?.totalEvents ?? 0) - (sa?.totalEvents ?? 0)
      if (deploymentSort === 'passRate') return (sb?.passRate ?? 0) - (sa?.passRate ?? 0)
      if (deploymentSort === 'aiUsage') return (sb?.aiUsage ?? 0) - (sa?.aiUsage ?? 0)
      if (deploymentSort === 'date') return new Date(b.date) - new Date(a.date)
      return 0
    })
    return result
  }, [deploymentSearch, courseFilter, deploymentSummaries, deploymentSort])

  return (
    <div style={{ ...styles.page, flexDirection: isMobile ? 'column' : 'row' }}>
      {/* ── Sidebar ── */}
      <div style={{ ...styles.sidebar, ...(isMobile ? styles.sidebarMobile : {}) }}>
        <div style={{ ...styles.sidebarHeader, ...(isMobile ? styles.sidebarHeaderMobile : {}) }}>
          <div style={styles.logoMark}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5" />
              <path d="M2 12l10 5 10-5" />
            </svg>
          </div>
          <span style={styles.logoText}>TutorTrace</span>
        </div>

        <div style={{ ...styles.sidebarNav, ...(isMobile ? styles.sidebarNavMobile : {}) }}>
          {MODES.map(m => {
            const Icon = m.icon
            const active = mode === m.key
            return (
              <button key={m.key} onClick={() => { setMode(m.key); setSelectedDeployment(null) }}
                disabled={!m.ready}
                style={{ ...styles.navItem, ...(isMobile ? styles.navItemMobile : {}), ...(active ? styles.navItemActive : {}), ...(!m.ready ? styles.navItemDisabled : {}) }}
                onMouseEnter={e => { if (m.ready && !active) e.currentTarget.style.background = '#f5f5f5' }}
                onMouseLeave={e => { if (m.ready && !active) e.currentTarget.style.background = 'transparent' }}
              >
                <Icon active={active} />
                <span>{m.label}</span>
                {!m.ready && <span style={styles.comingSoon}>Soon</span>}
              </button>
            )
          })}
        </div>

        <div style={{ ...styles.pinnedSection, ...(isMobile ? styles.pinnedSectionMobile : {}) }}>
          <button onClick={() => {
            const allDeployments = DEPLOYMENTS.map(dep => {
              const data = deploymentData[dep.id]
              if (!data?.eventsByUser) return null
              return {
                deployment: dep.id, session_id: dep.sessionId, section_id: dep.sectionId,
                course: dep.course, language: dep.language, date: dep.date, time: dep.time,
                student_count: Object.keys(data.eventsByUser).length,
                total_events: Object.values(data.eventsByUser).flat().length,
                students: Object.entries(data.eventsByUser).map(([userId, events]) => {
                  const student = (data.students || []).find(s => String(s.id) === String(userId))
                  return { student_id: userId, name: student?.display_name || student?.username || `Student ${userId}`, event_count: events.length, events }
                }),
              }
            }).filter(Boolean)
            if (allDeployments.length === 0) { alert('No deployment data loaded yet.'); return }
            const blob = new Blob([JSON.stringify({ exported_at: new Date().toISOString(), deployment_count: allDeployments.length, total_students: allDeployments.reduce((s, d) => s + d.student_count, 0), total_events: allDeployments.reduce((s, d) => s + d.total_events, 0), deployments: allDeployments }, null, 2)], { type: 'application/json' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a'); a.href = url; a.download = `tutortrace_all_deployments_${new Date().toISOString().slice(0, 10)}.json`; a.click(); URL.revokeObjectURL(url)
          }} style={styles.pinnedItem}
            onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            <span>Export All Data</span>
          </button>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div style={styles.main}>

        {/* ── About ── */}
        {mode === 'about' && <AboutPage onExplore={() => setMode('datasets')} />}

        {/* ── Applications ── */}
        {mode === 'applications' && <ApplicationsPage />}

        {/* ── Datasets ── */}
        {mode === 'datasets' && (
          <>
            <div style={{ ...styles.topBar, ...(isMobile ? styles.topBarMobile : {}) }}>
              {selectedDeployment ? (
                <div style={styles.breadcrumb}>
                  <button onClick={() => setSelectedDeployment(null)} style={styles.breadcrumbLink}>Datasets</button>
                  <span style={styles.breadcrumbSep}>›</span>
                  <span style={styles.breadcrumbCurrent}>{COURSE_DISPLAY[selectedDeployment.language]?.name || selectedDeployment.course} · {selectedDeployment.label}</span>
                </div>
              ) : (
                <div><h1 style={styles.pageTitle}>Datasets</h1></div>
              )}
            </div>

            {!selectedDeployment ? (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: isMobile ? '0 16px' : '0 32px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', paddingTop: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #e0e0e3', background: '#fff', flex: 1 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <input type="text" placeholder="Search deployments..." value={deploymentSearch} onChange={e => setDeploymentSearch(e.target.value)}
                      style={{ border: 'none', outline: 'none', fontSize: '14px', color: '#333', background: 'transparent', width: '100%' }} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #e0e0e3', background: '#fff' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="6" x2="16" y2="6"/><line x1="4" y1="12" x2="12" y2="12"/><line x1="4" y1="18" x2="8" y2="18"/></svg>
                    <select value={deploymentSort} onChange={e => setDeploymentSort(e.target.value)} style={{ border: 'none', outline: 'none', fontSize: '13px', color: '#444', background: 'transparent', cursor: 'pointer', fontWeight: 500 }}>
                      <option value="students">Students</option>
                      <option value="events">Events</option>
                      <option value="passRate">Pass Rate</option>
                      <option value="aiUsage">AI Usage</option>
                      <option value="date">Date</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
                  <button onClick={() => setCourseFilter('all')} style={{
                    padding: '6px 16px', borderRadius: '20px',
                    border: courseFilter === 'all' ? '1.5px solid #111' : '1.5px solid #e0e0e3',
                    background: courseFilter === 'all' ? '#111' : '#fff',
                    color: courseFilter === 'all' ? '#fff' : '#555',
                    fontSize: '13px', fontWeight: 500, cursor: 'pointer',
                  }}>All</button>
                  {uniqueLanguages.map(lang => (
                    <button key={lang} onClick={() => setCourseFilter(courseFilter === lang ? 'all' : lang)} style={{
                      padding: '6px 16px', borderRadius: '20px',
                      border: courseFilter === lang ? '1.5px solid #111' : '1.5px solid #e0e0e3',
                      background: courseFilter === lang ? '#111' : '#fff',
                      color: courseFilter === lang ? '#fff' : '#555',
                      fontSize: '13px', fontWeight: 500, cursor: 'pointer',
                    }}>{COURSE_DISPLAY[lang]?.name || lang}</button>
                  ))}
                  <span style={{ fontSize: '12px', color: '#bbb', marginLeft: 'auto', fontWeight: 500 }}>
                    {filteredDeployments.length} of {DEPLOYMENTS.length}
                  </span>
                </div>

                <div style={{ ...styles.deploymentGrid, ...(isMobile ? { gridTemplateColumns: '1fr' } : {}) }}>
                  {filteredDeployments.map(dep => (
                    <DeploymentCard key={dep.id} deployment={dep} summary={deploymentSummaries[dep.id]}
                      loading={deploymentData[dep.id]?.loading} onClick={() => handleSelectDeployment(dep)}
                      onDownload={handleDownloadDeployment} />
                  ))}
                </div>
              </div>
            ) : (
              <SessionList deployment={selectedDeployment} data={deploymentData[selectedDeployment.id]}
                onLoadDeployment={() => loadDeployment(selectedDeployment)} caseStudyData={caseStudyData} />
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ─── Styles ─────────────────────────────────────────────────────────────────────

const styles = {
  page: { display: 'flex', height: '100vh', overflow: 'hidden', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", backgroundColor: '#ffffff' },
  sidebar: { width: '230px', backgroundColor: '#ffffff', display: 'flex', flexDirection: 'column', flexShrink: 0, borderRight: '1px solid #e8e8eb' },
  sidebarMobile: { width: '100%', flexDirection: 'row', alignItems: 'center', borderRight: 'none', borderBottom: '1px solid #e8e8eb', overflowX: 'auto', padding: '0 8px', gap: '4px' },
  sidebarHeaderMobile: { padding: '12px 8px', flexShrink: 0 },
  sidebarNavMobile: { flexDirection: 'row', flex: '0 0 auto', padding: '6px 2px', gap: '2px' },
  navItemMobile: { width: 'auto', whiteSpace: 'nowrap', flexShrink: 0 },
  pinnedSectionMobile: { borderTop: 'none', padding: '6px', marginLeft: 'auto', flexShrink: 0 },
  topBarMobile: { padding: '16px 16px 14px' },
  sidebarHeader: { display: 'flex', alignItems: 'center', gap: '10px', padding: '20px 18px 16px' },
  logoMark: { width: '30px', height: '30px', borderRadius: '8px', background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 700, color: '#fff' },
  logoText: { fontSize: '16px', fontWeight: 700, letterSpacing: '-0.02em', color: '#111' },
  sidebarNav: { display: 'flex', flexDirection: 'column', gap: '1px', padding: '4px 10px', flex: 1 },
  navItem: { display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '8px', border: 'none', background: 'transparent', color: '#666', fontSize: '14px', fontWeight: 500, cursor: 'pointer', transition: 'all 0.12s', textAlign: 'left', width: '100%' },
  navItemActive: { backgroundColor: '#f3f3f3', color: '#111', fontWeight: 600 },
  navItemDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  comingSoon: { marginLeft: 'auto', fontSize: '9px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#bbb', background: '#f5f5f5', padding: '2px 6px', borderRadius: '4px' },
  pinnedSection: { padding: '10px 10px 12px', borderTop: '1px solid #f0f0f2' },
  pinnedItem: { display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderRadius: '8px', border: 'none', background: 'transparent', color: '#666', fontSize: '13px', fontWeight: 500, cursor: 'pointer', width: '100%', textAlign: 'left', transition: 'background 0.12s' },
  main: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#fafafa' },
  topBar: { padding: '24px 32px 0', flexShrink: 0, backgroundColor: '#ffffff', borderBottom: '1px solid #e8e8eb', paddingBottom: '20px' },
  breadcrumb: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' },
  breadcrumbLink: { background: 'none', border: 'none', padding: 0, fontSize: '14px', color: '#888', cursor: 'pointer', fontWeight: 500 },
  breadcrumbSep: { color: '#ccc', fontSize: '16px' },
  breadcrumbCurrent: { color: '#111', fontWeight: 600 },
  pageTitle: { fontSize: '24px', fontWeight: 700, color: '#111', margin: 0, letterSpacing: '-0.02em' },
  deploymentGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px', overflowY: 'auto', flex: 1, paddingBottom: '24px' },
}